import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEvent, useContentPage, useUpdateContentPage, useDeleteContentPage } from '@/lib/api/queries'
import { useState, useEffect, useRef } from 'react'
import { usePermission } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'
import { formatDate } from '@/lib/date'
import { useAuth } from '@/lib/auth'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.eventpay.com/en/api'

export const Route = createFileRoute('/_authenticated/events/$eventId/event-content/$pageId/edit')({
  component: EditContentPage,
})

function EditContentPage() {
  const { eventId, pageId } = Route.useParams()
  const eventIdNum = Number(eventId)
  const pageIdNum = Number(pageId)
  const navigate = useNavigate()
  const { token } = useAuth()
  
  // Permission checks
  const canEditEventContent = usePermission('can_edit_event_content')
  const canDeleteEventContent = usePermission('can_delete_event_content')
  
  const { data: event } = useEvent(eventIdNum)
  const { data: contentPage, isLoading: pageLoading } = useContentPage(pageIdNum)
  const updateMutation = useUpdateContentPage()
  const deleteMutation = useDeleteContentPage()
  
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [isPublished, setIsPublished] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  
  const editorRef = useRef<HTMLTextAreaElement>(null)
  const editorInitialized = useRef(false)
  
  // Populate form when content page loads
  useEffect(() => {
    if (contentPage) {
      setTitle(contentPage.title)
      setContent(contentPage.content)
      setIsPublished(contentPage.is_published)
      
      // Update TinyMCE if initialized
      const tinymce = (window as unknown as { tinymce?: TinyMCE }).tinymce
      if (tinymce?.activeEditor) {
        tinymce.activeEditor.setContent(contentPage.content)
      }
    }
  }, [contentPage])
  
  // Initialize TinyMCE
  useEffect(() => {
    if (editorInitialized.current) return
    
    const initEditor = () => {
      if (typeof window === 'undefined' || !(window as unknown as { tinymce?: unknown }).tinymce) {
        // Load TinyMCE script if not loaded
        const script = document.createElement('script')
        script.src = 'https://cdn.jsdelivr.net/npm/tinymce@6/tinymce.min.js'
        script.referrerPolicy = 'origin'
        script.onload = () => {
          setTimeout(setupTinyMCE, 100)
        }
        document.head.appendChild(script)
      } else {
        setupTinyMCE()
      }
    }
    
    const setupTinyMCE = () => {
      const tinymce = (window as unknown as { tinymce: TinyMCE }).tinymce
      if (!tinymce) return
      
      tinymce.remove()
      
      tinymce.init({
        selector: '#content-editor',
        height: 500,
        menubar: 'file edit view insert format tools table',
        plugins: 'anchor autolink charmap codesample emoticons image link lists media searchreplace table visualblocks wordcount code fullscreen',
        toolbar: 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table | align lineheight | numlist bullist indent outdent | emoticons charmap | removeformat | code fullscreen',
        content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; font-size: 14px; }',
        skin: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'oxide-dark' : 'oxide',
        content_css: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'default',
        // Image upload configuration
        images_upload_url: `${API_BASE_URL}/upload-media/`,
        automatic_uploads: true,
        images_file_types: 'jpg,jpeg,png,gif,webp,svg',
        file_picker_types: 'image',
        // Custom upload handler with authentication
        images_upload_handler: (blobInfo: BlobInfo, progress: (percent: number) => void) => {
          return new Promise<string>((resolve, reject) => {
            const xhr = new XMLHttpRequest()
            xhr.open('POST', `${API_BASE_URL}/upload-media/`)
            
            if (token) {
              xhr.setRequestHeader('Authorization', `Token ${token}`)
            }
            
            xhr.upload.onprogress = (e) => {
              progress((e.loaded / e.total) * 100)
            }
            
            xhr.onload = () => {
              if (xhr.status === 403) {
                reject({ message: 'Authentication required', remove: true })
                return
              }
              
              if (xhr.status < 200 || xhr.status >= 300) {
                reject('HTTP Error: ' + xhr.status)
                return
              }
              
              const json = JSON.parse(xhr.responseText)
              if (!json || typeof json.location !== 'string') {
                reject('Invalid response: ' + xhr.responseText)
                return
              }
              
              resolve(json.location)
            }
            
            xhr.onerror = () => {
              reject('Image upload failed. Code: ' + xhr.status)
            }
            
            const formData = new FormData()
            formData.append('file', blobInfo.blob(), blobInfo.filename())
            xhr.send(formData)
          })
        },
        setup: (editor: TinyMCEEditor) => {
          editor.on('init', () => {
            if (contentPage) {
              editor.setContent(contentPage.content)
            }
          })
          editor.on('change', () => {
            setContent(editor.getContent())
          })
          editor.on('blur', () => {
            setContent(editor.getContent())
          })
        },
      })
      
      editorInitialized.current = true
    }
    
    initEditor()
    
    return () => {
      const tinymce = (window as unknown as { tinymce?: TinyMCE }).tinymce
      if (tinymce) {
        tinymce.remove()
      }
      editorInitialized.current = false
    }
  }, [contentPage])
  
  // Permission guard
  if (!canEditEventContent) {
    return <AccessDenied message="You don't have permission to edit content pages." />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    
    // Get content from TinyMCE
    const tinymce = (window as unknown as { tinymce?: TinyMCE }).tinymce
    const finalContent = tinymce?.activeEditor?.getContent() || content
    
    if (!title.trim()) {
      setError('Title is required.')
      return
    }
    
    if (!finalContent.trim()) {
      setError('Content is required.')
      return
    }
    
    try {
      await updateMutation.mutateAsync({
        pageId: pageIdNum,
        data: {
          title: title.trim(),
          content: finalContent,
          is_published: isPublished,
        },
      })
      
      navigate({
        to: '/events/$eventId/event-content',
        params: { eventId },
        search: { updated: 'true' },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update content page')
    }
  }

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(pageIdNum)
      navigate({
        to: '/events/$eventId/event-content',
        params: { eventId },
        search: { deleted: 'true' },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete content page')
      setShowDeleteConfirm(false)
    }
  }

  if (pageLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-neutral-200 dark:bg-neutral-800 rounded w-1/3"></div>
          <div className="h-12 bg-neutral-200 dark:bg-neutral-800 rounded"></div>
          <div className="h-[500px] bg-neutral-200 dark:bg-neutral-800 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Edit Content Page</h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
            Edit "{contentPage?.title}" for {event?.name}
          </p>
        </div>
        
        <Link
          to="/events/$eventId/event-content"
          params={{ eventId }}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 border border-neutral-300 dark:border-neutral-600 rounded-md hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to Content Pages
        </Link>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <div className="flex">
            <svg className="w-5 h-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800">
        <div className="p-4 border-b border-neutral-200 dark:border-neutral-800">
          <h3 className="text-lg font-medium text-neutral-900 dark:text-white">Content Page Details</h3>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Left Column: Form Fields */}
            <div className="space-y-6">
              {/* Title */}
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  placeholder="Enter page title..."
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                  A descriptive title for your content page
                </p>
              </div>

              {/* Content */}
              <div>
                <label htmlFor="content-editor" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Content *
                </label>
                <textarea
                  id="content-editor"
                  ref={editorRef}
                  defaultValue={content}
                  className="w-full min-h-[500px] px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white"
                />
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                  Use the editor to create your content. You can upload images, add formatting, and more.
                </p>
              </div>
            </div>

            {/* Right Column: Preview */}
            <div className="space-y-6">
              {contentPage?.content ? (
                <div className="bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-md p-4 h-full">
                  <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
                    Current Content Preview:
                  </h4>
                  <div 
                    className="prose prose-sm max-w-none dark:prose-invert text-neutral-600 dark:text-neutral-400 border border-neutral-200 dark:border-neutral-600 rounded p-4 bg-white dark:bg-neutral-900 h-[calc(100%-2.5rem)] overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: contentPage.content }}
                  />
                </div>
              ) : (
                <div className="bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-md p-4 h-full flex items-center justify-center">
                  <p className="text-neutral-500 dark:text-neutral-400 text-sm text-center">
                    Content preview will appear here after saving
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Published Status */}
          <div className="flex items-center mb-6">
            <input
              type="checkbox"
              id="is_published"
              checked={isPublished}
              onChange={(e) => setIsPublished(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded"
            />
            <label htmlFor="is_published" className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300">
              Published
            </label>
          </div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 -mt-4 ml-6 mb-6">
            Uncheck to save as draft
          </p>

          {/* Metadata */}
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-md p-4 mb-6">
            <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Page Information</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-neutral-600 dark:text-neutral-400">
              <div>
                <span className="font-medium">Created:</span>{' '}
                {contentPage?.created
                  ? formatDate(contentPage.created, 'MMM d, yyyy h:mm a')
                  : 'Unknown'}
              </div>
              <div>
                <span className="font-medium">Last Updated:</span>{' '}
                {contentPage?.updated
                  ? formatDate(contentPage.updated, 'MMM d, yyyy h:mm a')
                  : contentPage?.created
                  ? formatDate(contentPage.created, 'MMM d, yyyy h:mm a')
                  : 'Unknown'}
              </div>
              {contentPage?.slug && (
                <div className="sm:col-span-2">
                  <span className="font-medium">Slug:</span>{' '}
                  <span className="font-mono">{contentPage.slug}</span>
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-between items-center pt-4 border-t border-neutral-200 dark:border-neutral-800">
            {/* Left side: Delete button */}
            <div>
              {canDeleteEventContent && (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 border border-red-300 dark:border-red-600 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                  Delete Page
                </button>
              )}
            </div>
            
            {/* Right side: Cancel and Update buttons */}
            <div className="flex space-x-3">
              <Link
                to="/events/$eventId/event-content"
                params={{ eventId }}
                className="px-4 py-2 text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 border border-neutral-300 dark:border-neutral-600 rounded-md hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateMutation.isPending ? 'Updating...' : 'Update Content Page'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
              Delete Content Page?
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
              Are you sure you want to delete "{contentPage?.title}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 border border-neutral-300 dark:border-neutral-600 rounded-md hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hide TinyMCE promotion */}
      <style>{`
        .tox-promotion,
        .tox-promotion-link {
          display: none !important;
        }
      `}</style>
    </div>
  )
}

// TinyMCE types
interface TinyMCE {
  remove: () => void
  init: (options: TinyMCEOptions) => void
  activeEditor?: TinyMCEEditor
}

interface TinyMCEOptions {
  selector: string
  height: number
  menubar: string
  plugins: string
  toolbar: string
  content_style: string
  skin: string
  content_css: string
  images_upload_url?: string
  automatic_uploads?: boolean
  images_file_types?: string
  file_picker_types?: string
  images_upload_handler?: (blobInfo: BlobInfo, progress: (percent: number) => void) => Promise<string>
  setup?: (editor: TinyMCEEditor) => void
}

interface TinyMCEEditor {
  getContent: () => string
  setContent: (content: string) => void
  on: (event: string, callback: () => void) => void
}

interface BlobInfo {
  blob: () => Blob
  filename: () => string
}
